import {cloneElement, isValidElement, type ReactElement, type ReactNode} from "react";
import AiSparkleIcon from "@atlaskit/icon/core/ai-sparkle";
import AppsIcon from "@atlaskit/icon/core/apps";
import BoardIcon from "@atlaskit/icon/core/board";
import BookWithBookmarkIcon from "@atlaskit/icon/core/book-with-bookmark";
import BriefcaseIcon from "@atlaskit/icon/core/briefcase";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChartBarIcon from "@atlaskit/icon/core/chart-bar";
import ChartTrendUpIcon from "@atlaskit/icon/core/chart-trend-up";
import CheckCircleIcon from "@atlaskit/icon/core/check-circle";
import ComponentIcon from "@atlaskit/icon/core/component";
import DashboardIcon from "@atlaskit/icon/core/dashboard";
import DataFlowIcon from "@atlaskit/icon/core/data-flow";
import DatabaseIcon from "@atlaskit/icon/core/database";
import FlagIcon from "@atlaskit/icon/core/flag";
import FolderClosedIcon from "@atlaskit/icon/core/folder-closed";
import GlobeIcon from "@atlaskit/icon/core/globe";
import GridIcon from "@atlaskit/icon/core/grid";
import ImageIcon from "@atlaskit/icon/core/image";
import LibraryAtlaskitIcon from "@atlaskit/icon/core/library";
import ListChecklistIcon from "@atlaskit/icon/core/list-checklist";
import MarketplaceIcon from "@atlaskit/icon/core/marketplace";
import MegaphoneIcon from "@atlaskit/icon/core/megaphone";
import OfficeBuildingIcon from "@atlaskit/icon/core/office-building";
import PageIcon from "@atlaskit/icon/core/page";
import PagesIcon from "@atlaskit/icon/core/pages";
import PeopleGroupIcon from "@atlaskit/icon/core/people-group";
import PersonIcon from "@atlaskit/icon/core/person";
import PersonAddCoreIcon from "@atlaskit/icon/core/person-add";
import PersonOffboardIcon from "@atlaskit/icon/core/person-offboard";
import PersonWarningIcon from "@atlaskit/icon/core/person-warning";
import PhoneCoreIcon from "@atlaskit/icon/core/phone";
import PowerPlugIcon from "@atlaskit/icon/core/power-plug";
import PulseIcon from "@atlaskit/icon/core/pulse";
import ShapesIcon from "@atlaskit/icon/core/shapes";
import ShieldIcon from "@atlaskit/icon/core/shield";
import SmartLinkListIcon from "@atlaskit/icon/core/smart-link-list";
import StarStarredIcon from "@atlaskit/icon/core/star-starred";
import TableIcon from "@atlaskit/icon/core/table";
import TagIcon from "@atlaskit/icon/core/tag";
import ToolsIcon from "@atlaskit/icon/core/tools";
import TreeIcon from "@atlaskit/icon/core/tree";
import VehicleCarIcon from "@atlaskit/icon/core/vehicle-car";
import TextRephraseIcon from "@atlaskit/icon-lab/core/text-rephrase";
import TalentIcon from "@atlaskit/icon-lab/core/talent";
import ChartBarLineIcon from "@atlaskit/icon-lab/core/chart-bar-line";
import HomeIcon from "@vesta/aergia/svgs/core/home.svg?react";
import LibraryIcon from "@vesta/aergia/svgs/core/library.svg?react";
import ListBulletedIcon from "@vesta/aergia/svgs/core/list-bulleted.svg?react";
import TeamsIcon from "@vesta/aergia/svgs/core/teams.svg?react";
import WorkItemsIcon from "@vesta/aergia/svgs/core/work-items.svg?react";
import ChartTrendIcon from "@vesta/aergia/svgs/core/chart-trend.svg?react";
import type {
    LegacyNavIcon,
    NavIcon,
    ViewType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {
    resolveScaffoldIconExplorerName,
    type ScaffoldIconExplorerName,
} from "~/workspaceConfig/scaffold/scaffoldIconMapping";

const scaffoldIconCache = new Map<NavIcon, ReactNode>();
const viewIconCache = new Map<ViewType, ReactNode>();

function createIconFromExplorerName(name: ScaffoldIconExplorerName): ReactNode {
    switch (name) {
        case "ai-sparkle":
            return <AiSparkleIcon label="" />;
        case "apps":
            return <AppsIcon label="" />;
        case "book-with-bookmark":
            return <BookWithBookmarkIcon label="" />;
        case "briefcase":
            return <BriefcaseIcon label="" />;
        case "chart-bar":
            return <ChartBarIcon label="" />;
        case "chart-bar-line":
            return <ChartBarLineIcon label="" />;
        case "chart-trend-up":
            return <ChartTrendUpIcon label="" />;
        case "check-circle":
            return <CheckCircleIcon label="" />;
        case "component":
            return <ComponentIcon label="" />;
        case "dashboard":
            return <DashboardIcon label="" />;
        case "data-flow":
            return <DataFlowIcon label="" />;
        case "database":
            return <DatabaseIcon label="" />;
        case "flag":
            return <FlagIcon label="" />;
        case "folder-closed":
            return <FolderClosedIcon label="" />;
        case "globe":
            return <GlobeIcon label="" />;
        case "grid":
            return <GridIcon label="" />;
        case "image":
            return <ImageIcon label="" />;
        case "library":
            return <LibraryAtlaskitIcon label="" />;
        case "list-checklist":
            return <ListChecklistIcon label="" />;
        case "marketplace":
            return <MarketplaceIcon label="" />;
        case "megaphone":
            return <MegaphoneIcon label="" />;
        case "office-building":
            return <OfficeBuildingIcon label="" />;
        case "page":
            return <PageIcon label="" />;
        case "pages":
            return <PagesIcon label="" />;
        case "people-group":
            return <PeopleGroupIcon label="" />;
        case "person":
            return <PersonIcon label="" />;
        case "person-add":
            return <PersonAddCoreIcon label="" />;
        case "person-offboard":
            return <PersonOffboardIcon label="" />;
        case "phone":
            return <PhoneCoreIcon label="" />;
        case "power-plug":
            return <PowerPlugIcon label="" />;
        case "pulse":
            return <PulseIcon label="" />;
        case "shapes":
            return <ShapesIcon label="" />;
        case "shield":
            return <ShieldIcon label="" />;
        case "smart-link-list":
            return <SmartLinkListIcon label="" />;
        case "star-starred":
            return <StarStarredIcon label="" />;
        case "table":
            return <TableIcon label="" />;
        case "tag":
            return <TagIcon label="" />;
        case "tools":
            return <ToolsIcon label="" />;
        case "tree":
            return <TreeIcon label="" />;
        case "vehicle-car":
            return <VehicleCarIcon label="" />;
    }
}

function createLegacyScaffoldIcon(icon: LegacyNavIcon): ReactNode {
    switch (icon) {
        case "home":
            return <HomeIcon className="fill-current" />;
        case "dayPlan":
            return <WorkItemsIcon className="fill-current" />;
        case "dashboard":
            return <DashboardIcon label="Analytical Dashboard" color="currentColor" />;
        case "personWarning":
            return <PersonWarningIcon label="" color="currentColor" />;
        case "textRephrase":
            return <TextRephraseIcon label="" color="currentColor" />;
        case "talent":
            return <TalentIcon label="" color="currentColor" />;
        case "personAdd":
            return <PersonAddCoreIcon label="" color="currentColor" />;
        case "phone":
            return <PhoneCoreIcon label="" color="currentColor" />;
        case "tools":
            return <ToolsIcon label="" color="currentColor" />;
        case "peopleGroup":
            return <TeamsIcon className="fill-current" />;
        case "workItems":
            return <WorkItemsIcon className="fill-current" />;
        case "reports":
            return <LibraryIcon className="fill-current" />;
        case "dataFlow":
            return <ChartTrendIcon className="fill-current" />;
        case "library":
            return <LibraryAtlaskitIcon label="" />;
        case "shapes":
            return <ShapesIcon label="" />;
        case "pages":
            return <PagesIcon label="" />;
        case "database":
            return <DatabaseIcon label="" />;
        case "component":
            return <ComponentIcon label="" />;
        case "officeBuilding":
            return <OfficeBuildingIcon label="" />;
        case "image":
            return <ImageIcon label="" />;
        case "bookBookmark":
            return <BookWithBookmarkIcon label="" />;
        case "personOffboard":
            return <PersonOffboardIcon label="" />;
    }
}

function isLegacyNavIcon(icon: NavIcon): icon is LegacyNavIcon {
    return (
        icon === "home" ||
        icon === "dayPlan" ||
        icon === "dashboard" ||
        icon === "personWarning" ||
        icon === "textRephrase" ||
        icon === "talent" ||
        icon === "personAdd" ||
        icon === "phone" ||
        icon === "tools" ||
        icon === "peopleGroup" ||
        icon === "workItems" ||
        icon === "reports" ||
        icon === "dataFlow" ||
        icon === "library" ||
        icon === "shapes" ||
        icon === "pages" ||
        icon === "database" ||
        icon === "component" ||
        icon === "officeBuilding" ||
        icon === "image" ||
        icon === "bookBookmark" ||
        icon === "personOffboard"
    );
}

function withInheritedIconColor(icon: ReactNode): ReactNode {
    if (isValidElement(icon) !== true) {
        return icon;
    }
    return cloneElement(icon as ReactElement<{color?: string}>, {color: "currentColor"});
}

function createScaffoldIcon(icon: NavIcon): ReactNode {
    const explorerName = resolveScaffoldIconExplorerName(icon);
    if (explorerName !== null) {
        return withInheritedIconColor(createIconFromExplorerName(explorerName));
    }
    if (isLegacyNavIcon(icon) === true) {
        return withInheritedIconColor(createLegacyScaffoldIcon(icon));
    }
    return withInheritedIconColor(<DashboardIcon label="" />);
}

export function resolveScaffoldIcon(icon: NavIcon): ReactNode {
    const cached = scaffoldIconCache.get(icon);
    if (cached !== undefined) {
        return cached;
    }
    const resolved = createScaffoldIcon(icon);
    scaffoldIconCache.set(icon, resolved);
    return resolved;
}

function createViewIcon(view: ViewType): ReactNode {
    switch (view) {
        case "table":
            return <TableIcon label="" />;
        case "list":
            return <ListBulletedIcon className="fill-current" />;
        case "kanban":
            return <BoardIcon label="" />;
        case "calendar":
            return <CalendarIcon label="" />;
        case "focus":
            return <DashboardIcon label="" />;
        default:
            return <DashboardIcon label="" />;
    }
}

export function resolveViewIcon(view: ViewType): ReactNode {
    const cached = viewIconCache.get(view);
    if (cached !== undefined) {
        return cached;
    }
    const resolved = createViewIcon(view);
    viewIconCache.set(view, resolved);
    return resolved;
}

export function viewLabel(view: ViewType): string {
    switch (view) {
        case "table":
            return "Table";
        case "list":
            return "List";
        case "kanban":
            return "Kanban";
        case "calendar":
            return "Calendar";
        case "focus":
            return "Focus";
        default:
            return "View";
    }
}
